import { logger } from '@luna/logger';

/**
 * Generates a URL path by substituting parameters into a path template.
 *
 * @param {string} path - The path template with parameter placeholders (e.g., "/user/:id")
 * @param {Record<string, string | undefined | null>} params - An object containing parameter values
 * @returns {string} The generated path with parameters substituted
 *
 * @example
 * ```typescript
 * // Basic usage
 * console.log(generatePath('/user/:id', { id: '123' }));
 * // Output: "/user/123"
 *
 * // Optional parameters
 * console.log(generatePath('/user/:id?/profile', { id: undefined }));
 * // Output: "/profile"
 *
 * // Wildcard parameter
 * console.log(generatePath('/files/*', { '*': 'path/to/file.txt' }));
 * // Output: "/files/path/to/file.txt"
 *
 * // Multiple parameters
 * console.log(generatePath('/user/:userId/post/:postId', { userId: '123', postId: '456' }));
 * // Output: "/user/123/post/456"
 * ```
 *
 * @throws {Error} In development mode, throws an error if any parameters are unresolved
 *
 * @remarks
 * - Optional parameters are denoted with a "?" (e.g., ":param?")
 * - The "*" parameter is a special case that allows undefined/null values and defaults to an empty string
 * - Trailing slashes are removed from the generated path
 */
export const generatePath = (
    path: string,
    params: Record<string, string | undefined | null>,
): string => {
    let result = path;
    Object.entries(params).forEach(([key, value]) => {
        // "*" is special case where undefined/null is allowed and fallbacks to empty string
        if (key === '*') {
            result = result.replaceAll('*', value || '');
            return;
        }
        if (value === null || value === undefined) {
            return;
        }
        result = result
            .replace(new RegExp(`:${key}\\?$`, 'g'), value)
            .replace(new RegExp(`:${key}\\?/`, 'g'), `${value}/`)
            .replace(new RegExp(`:${key}\\?`, 'g'), `${value}?`)
            .replace(new RegExp(`:${key}`, 'g'), value);
    });

    // remove optional parameters and "/" suffix
    result = result
        .replace(/(\/|^):[a-z0-9]+\?\//gi, '/')
        .replace(/(\/|^):[a-z0-9]+\?$/gi, '')
        .replace(/\/$/, '');

    if (result.match(/:[a-z]+/gi)) {
        const errorMessage = `Result of generatePath "${result}" has unresolved value`;
        if (__DEV__) {
            throw new Error(errorMessage);
        }
        logger.error(errorMessage);
    }

    return result;
};
